import { TranslationKey } from '../../../../types/translation/TranslationKey';
import { StockLocation } from '../../../../types/stock_location';
import { TableHeader } from '..';
import { toMap } from '../../../../util/map.util';
import { CustomField, CustomFieldEntityType, CustomFieldType } from '../../../../types/custom_field/customField';
import {
  ProductDetailColumn,
  ProductDetailTableSettings,
} from '../../../../types/product_table_settings/productDetailTableSettings';
import { Product } from '../../../../types/product';
import { ProductInfoPaneTableHeaderKey } from '../../../../util/identifiers/ProductInfoPaneTableHeaderKey';
import { t } from '../../../../types/translation/Translator';
import { Bin } from '../../../../types/bin/bin';
import { ProductMasterData } from '../../../../types/product_master_data/productMasterData';
import { Container } from '../../../../types/container';
import dayjs from 'dayjs';
import { Lot } from '../../../../types/lot/lot';
import { UnitOfMeasure, UnitOfMeasureUtil } from '../../../../types/uom';
import { Kit } from '../../../../types/kit/kit';
import { KitTemplate } from '../../../../types/kit_template/kitTemplate';
import { Company } from '../../../../types/company';
import utc from 'dayjs/plugin/utc';

dayjs.extend(utc);

export const productDetailTableHeaders = (
  items: Product[],
  products: Map<string, Map<string, Product>>,
  stockLocations: Map<string, StockLocation>,
  bins: Map<string, Bin>,
  containers: Map<string, Container>,
  productMasterData: Map<string, ProductMasterData>,
  lots: Map<string, Lot>,
  customFields: Map<string, CustomField>,
  handlingUnitConnections: Map<string, Set<string>>,
  kits: Map<string, Kit>,
  kitTemplates: Map<string, KitTemplate>,
  settings: ProductDetailTableSettings,
  companies: Map<string, Company>,
  company: Company,
  locale: string,
  productMasterDataId?: string,
) => {
  const wantedColumns = toMap(settings.columns, 'column');

  const columns: TableHeader<Product>[] = [];

  const hasLots = items.some(p => p.lotId);
  const hasSerial = items.some(p => p.serial);
  const hasLpn = items.some(p => p.lpn);
  const hasContainer = items.some(p => p.containerId);
  const hasKit = items.some(p => p.kitId);

  const connections = handlingUnitConnections.get(productMasterDataId || '');

  const isPartner = company.partner;

  if (isPartner) {
    columns.push({
      key: 'company',
      name: t(TranslationKey.company).singular.label,
      text: (item: Product) => companies.get(item.companyId)?.name || '',
    });
  }

  if (wantedColumns.has(ProductDetailColumn.productName) || connections?.size) {
    columns.push({
      key: ProductInfoPaneTableHeaderKey.productName,
      column: ProductDetailColumn.productName,
      name: t(TranslationKey.productName).singular.label,
      text: (item: Product) =>
        productMasterData.get(item.productMasterDataId)?.productName || 'Unknown Product Master Data',
    });
  }

  if (wantedColumns.has(ProductDetailColumn.productNumber) || connections?.size) {
    columns.push({
      key: ProductInfoPaneTableHeaderKey.productNumber,
      column: ProductDetailColumn.productNumber,
      name: t(TranslationKey.productNumber).singular.label,
      text: (item: Product) =>
        productMasterData.get(item.productMasterDataId)?.productNumber || 'Unknown Product Master Data',
    });
  }

  if (wantedColumns.has(ProductDetailColumn.stockLocation)) {
    columns.push({
      key: ProductInfoPaneTableHeaderKey.stockLocation,
      column: ProductDetailColumn.stockLocation,
      name: t(TranslationKey.stockLocation).singular.label,
      text: (item: Product) => stockLocations.get(item.stockLocationId)?.name || 'Unknown Stock Location',
    });
  }

  if (wantedColumns.has(ProductDetailColumn.bin)) {
    columns.push({
      key: ProductInfoPaneTableHeaderKey.bin,
      column: ProductDetailColumn.bin,
      name: t(TranslationKey.bin).singular.label,
      text: (item: Product) => bins.get(item.binId)?.name || 'Unknown Bin',
    });
  }

  if (hasContainer && wantedColumns.has(ProductDetailColumn.container)) {
    columns.push({
      key: ProductInfoPaneTableHeaderKey.container,
      column: ProductDetailColumn.container,
      name: t(TranslationKey.container).singular.label,
      text: (item: Product) =>
        !item.containerId
          ? ''
          : containers.get(item.containerId)?.identifier || t(TranslationKey.unknownContainer).singular.label,
    });
  }

  if (hasLpn && wantedColumns.has(ProductDetailColumn.lpn)) {
    columns.push({
      key: ProductInfoPaneTableHeaderKey.lpn,
      column: ProductDetailColumn.lpn,
      name: t(TranslationKey.lpn).singular.label,
      text: (item: Product) => item.lpn || 'No LPN',
    });
  }

  if (hasSerial && wantedColumns.has(ProductDetailColumn.serial)) {
    columns.push({
      key: ProductInfoPaneTableHeaderKey.serialNumber,
      column: ProductDetailColumn.serial,
      name: t(TranslationKey.serial).singular.label,
      text: (item: Product) => item.serial || 'No serial',
    });
  }

  if (hasKit && !hasSerial && wantedColumns.has(ProductDetailColumn.kit)) {
    columns.push({
      key: ProductInfoPaneTableHeaderKey.kit,
      column: ProductDetailColumn.kit,
      name: t(TranslationKey.kit).singular.label,
      text: (item: Product) => kits.get(item.kitId || '')?.identifier || '',
    });
  }

  if (hasLots && wantedColumns.has(ProductDetailColumn.lotNumber)) {
    columns.push({
      key: ProductInfoPaneTableHeaderKey.lotNumber,
      column: ProductDetailColumn.lotNumber,
      name: t(TranslationKey.lotNumber).singular.label,
      text: (item: Product) => lots.get(item.lotId || '')?.number || 'No Lot',
    });
  }

  if (hasLots && wantedColumns.has(ProductDetailColumn.lotExpiry)) {
    columns.push({
      key: ProductInfoPaneTableHeaderKey.lotExpiry,
      column: ProductDetailColumn.lotExpiry,
      name: t(TranslationKey.expirationDate).singular.label,
      sortValue: (item: Product) => lots.get(item.lotId || '')?.expirationDate || '',
      text: (item: Product) => {
        const lot = lots.get(item.lotId || '');
        if (!lot) return '';
        if (!lot.expirationDate) return '';

        const expiration = dayjs(lot.expirationDate);
        return expiration.isValid() ? expiration.toDate().toLocaleDateString(locale) : '';
      },
    });
  }

  const pmd = productMasterData.get(productMasterDataId || '');

  const minId = '848031c6-7e2f-444b-9903-8937e8f288f1';
  const maxId = 'd801e294-cc3d-4a13-b6e3-b31e028e4fd2';

  const max = pmd?.customFields.get(maxId)?.value || '0';
  const min = pmd?.customFields.get(minId)?.value || '0';

  columns.push({
    key: 'du',
    column: 'du',
    name: 'DU',
    text: (item: Product) => {
      const stockLocation = stockLocations.get(item.stockLocationId);
      if (!stockLocation) return '';
      const du = stockLocation.customFields.get('7a8e2f9c-a68f-4ac1-91ba-298d33c60882')?.value || '';
      return du;
    },
  });

  columns.push({
    key: 'min',
    column: 'min',
    name: 'Min',
    text: (item: Product) => {
      return min;
    },
  });

  columns.push({
    key: 'max',
    column: 'max',
    name: 'Max',
    text: (item: Product) => {
      return max;
    },
  });

  columns.push({
    key: 'surplus',
    column: 'surplus',
    name: 'Surplus',
    text: (item: Product) => {
      let quantity = parseFloat(UnitOfMeasure.toQuantity(pmd?.unitOfMeasure, item.unitQuantity)) - parseFloat(min);
      if (quantity < 0) quantity = 0;
      return quantity;
    },
  });

  if (wantedColumns.has(ProductDetailColumn.quantity)) {
    columns.push({
      key: ProductInfoPaneTableHeaderKey.quantity,
      column: ProductDetailColumn.quantity,
      name: 'On Hand',
      // name: t(TranslationKey.quantity).singular.label,
      text: (item: Product) => {
        const pmd = productMasterData.get(item.productMasterDataId);

        if (pmd?.kitManaged) {
          const kit = kits.get(item.kitId || '');
          const kitTemplate = kitTemplates.get(kit?.templateId || '');

          const total = kitTemplate?.products?.reduce((sum, kp) => sum + kp.quantity, 0) || 1;
          const available = [...products.values()]
            .flatMap(m => [...m.values()])
            .filter(p => p.kitId === item.kitId && p.productMasterDataId !== item.productMasterDataId)
            .reduce((sum, p) => {
              const i = productMasterData.get(p.productMasterDataId);
              return sum + parseFloat(UnitOfMeasure.toQuantity(i?.unitOfMeasure, p.unitQuantity));
            }, 0);

          return `${((available / total) * 100).toFixed(0).toString()}%`;
        }

        if (item.productMasterDataId === productMasterDataId) {
          return `${UnitOfMeasure.toQuantityWithSuffix(pmd?.unitOfMeasure, item.unitQuantity)}`;
        }

        if (!connections || !connections.size)
          return `${UnitOfMeasure.toQuantityWithSuffix(pmd?.unitOfMeasure, item.unitQuantity)}`;

        const connection = [...connections.values()].find(c => c.includes(`${item.productMasterDataId}::`));
        if (!connection) return `${UnitOfMeasure.toQuantityWithSuffix(pmd?.unitOfMeasure, item.unitQuantity)}`;

        const [parentId, multiplier] = connection.split('::');
        return `${UnitOfMeasure.toQuantity(pmd?.unitOfMeasure, item.unitQuantity)} (${UnitOfMeasure.toQuantity(
          pmd?.unitOfMeasure,
          (BigInt(item.unitQuantity) * BigInt(multiplier)).toString(),
        )}) ${UnitOfMeasureUtil.getSuffix(pmd?.unitOfMeasure)}`;
      },
    });
  }

  const productCustomFieldIdsInProductsArr = new Set(items.flatMap(p => [...p.getAllIdentifierValues().keys()]));
  const productCustomFields = [...customFields.values()].filter(
    cf => cf.entityType === CustomFieldEntityType.product && productCustomFieldIdsInProductsArr.has(cf.id),
  );

  productCustomFields.forEach((cf, idx) => {
    const name = `cf-${cf.name}`;
    wantedColumns.set(name, { column: name, index: 99 - idx, label: cf.name });
    columns.push({
      key: cf.name,
      column: name,
      name: cf.name,
      text: (product: Product) => {
        const value = product.getIdentifierValue(cf.id) || '';

        if (cf.type === CustomFieldType.date && value) {
          return dayjs(value).toDate().toLocaleDateString(locale);
        }
        return value;
      },
    });
  });

  // return columns.sort(
  //   (a: TableHeader<Product>, b: TableHeader<Product>) =>
  //     (wantedColumns.get(a.column || '')?.index || -1) - (wantedColumns.get(b.column || '')?.index || -1),
  // );

  return columns;
};
